import { 
  Input,
  OnInit,
  Output,
  Component,
  OnChanges,
  EventEmitter,
  SimpleChanges,
  ChangeDetectionStrategy
} from '@angular/core';
import { NgClass, SlicePipe } from '@angular/common';

/**
 * enums
 */
import { IThemeColorVariant } from '@core/enums/theme-colors-variants';
import { IPaginationSize } from '@sharedComponents/pagination/enums/pagination-size';

/**
 * directives
 */
import { WavesEffectDirective } from '@sharedDirectives/node-waves/waves-effect.directive';

/**
 * @component PaginationComponent
 * @description
 * Angular Bootstrap-style pagination component.
 * Renders a dynamic list of page buttons with optional navigation (prev/next),
 * jump controls (first/last page, first/last row), and icon customization.
 * Supports Bootstrap theming, rounded/square variants, and emits navigation events.
 *
 * Utilizes Angular v17+ control flow syntax (`@if`, `@for`) in template.
 *
 * @example
 * <app-pagination
 *    [currentPage]="1"
 *    [itemsPerPage]="10"
 *    [totalRecords]="100"
 *    [variant]="'primary'"
 *    [jumpButton]="true"
 *    [navigationButton]="true"
 *    [jumpButtonInRow]="true"
 *    [firstIcon]="'fa fa-angle-double-left'"
 *    [lastIcon]="'fa fa-angle-double-right'"
 *    (currentPageChange)="onPageChanged($event)">
 * </app-pagination>
 */
@Component({
  selector: 'app-pagination',
  templateUrl: './pagination.component.html',
  styleUrl: './pagination.component.scss',
  exportAs: 'paginationApi',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    SlicePipe,
    /**
     * directives
     */
    WavesEffectDirective,
  ],
})
export class PaginationComponent implements OnChanges, OnInit {

  /**
   * @input ariaLabel
   * @description Accessible label for the pagination navigation.
   * @default "Page navigation"
   */
  @Input({ alias: 'ariaLabel' })
  public ariaLabel: string = "Page navigation"

  /**
   * @input currentPage
   * @description Current active page. Automatically clamped between 1 and lastPage.
   * @default 1
   */
  @Input({ alias: 'currentPage' })
  public set currentPage(value: number) {

    const _outOfRange = 
    value == null
    || typeof value !== 'number'
    || value > this.lastPage
    || value < 1;
    
    // void
    if (_outOfRange) return;

    this._currentPage = value;

  }
  public get currentPage(): number {
    return this._currentPage;
  }
  private _currentPage: number = 1;

  /**
   * @input itemsPerPage
   * @description Number of items per page.
   * @default 10
   */
  @Input({ alias: 'itemsPerPage' })
  public itemsPerPage: number = 10;

  /**
   * @input totalRecords
   * @description Total number of records.
   * @required
   */
  @Input({ alias: 'totalRecords', required: true })
  public totalRecords: number = 0;

  /**
   * @input rows
   * @description Number of visible page buttons at a time.
   * @default 5
   */
  @Input({ alias: 'rows' })
  public rows: number = 5;

  /**
   * @input variant
   * @description Bootstrap color variant for the pagination.
   */
  @Input({ alias: 'variant' })
  public variant!: IThemeColorVariant;

  /**
   * @input size
   * @description Size of the pagination (e.g., sm, lg).
   */
  @Input({ alias: 'size' })
  public size!: IPaginationSize;

  /**
   * @input outline
   * @description Applies Bootstrap outline style.
   * @default false
   */
  @Input({ alias: 'outline' })
  public isOutline: boolean = false;

  /**
   * @input rounded
   * @description Applies rounded style.
   * @default false
   */
  @Input({ alias: 'rounded' })
  public rounded: boolean = false;

  /**
   * @input square
   * @description Applies square style.
   * @default false
   */
  @Input({ alias: 'square' })
  public square: boolean = false;

  /**
   * @input disabled
   * @description Disables the pagination.
   * @default false
   */
  @Input({ alias: 'disabled' })
  public disabled: boolean = false;

  /**
   * @input navigationButton
   * @description Enables next/previous navigation buttons.
   * @default true
   */
  @Input({ alias: 'navigationButton' })
  public navigationButton: boolean = true;

  /**
   * @input jumpButton
   * @description
   * Enables rendering of "jump to first" and "jump to last" buttons in the template.
   * These buttons appear conditionally and may display icons or fallback to numbers.
   */
  @Input({ alias: 'jumpButton' })
  public jumpButton: boolean = false;

  /**
   * @input jumpButtonInRow
   * @description
   * Enables buttons to jump between visible row groups (e.g., 1–5 → 6–10).
   * Uses `fistRowIcon` and `lastRowIcon` for visual cues.
   */
  @Input({ alias: 'jumpButtonInRow' })
  public jumpButtonInRow: boolean = true;

  /**
   * @input nextIcon
   * @description Icon class for the "Next" button.
   */
  @Input({ alias: 'nextIcon' })
  public nextIcon: string = "fa-regular fa-chevron-left fa-sm";

  /**
   * @input fistRowIcon
   * @description Icon class for jump-to-first-row button.
   */
  @Input({ alias: 'fistRowIcon' })
  public fistRowIcon: string = "fa-regular fa-chevrons-left fa-sm";

  /**
   * @input previousIcon
   * @description Icon class for the "Previous" button.
   */
  @Input({ alias: 'previousIcon' })
  public previousIcon: string = "fa-regular fa-chevron-right fa-sm";

  /**
   * @input lastRowIcon
   * @description Icon class for jump-to-last-row button.
   */
  @Input({ alias: 'lastRowIcon' })
  public lastRowIcon: string = "fa-regular fa-chevrons-right fa-sm";

  /**
   * @input firstIcon
   * @description
   * Optional icon class for the "first page" button.
   * If not provided, fallback value (1) will be shown.
   */
  @Input({ alias: 'firstIcon' })
  public firstIcon!: string;

  /**
   * @input lastIcon
   * @description
   * Optional icon class for the "last page" button.
   * If not provided, fallback value ({{ lastPage }}) will be shown.
   */
  @Input({ alias: 'lastIcon' })
  public lastIcon!: string;

  /**
   * @output currentPageChange
   * @description Emits the current page when it changes.
   */
  @Output('currentPageChange')
  public pageChange: EventEmitter<number> = new EventEmitter<number>();

  /**
   * @output first
   * @description Emits when the first page is reached.
   */
  @Output('first')
  public firstMount: EventEmitter<number> = new EventEmitter<number>();

  /**
   * @output last
   * @description Emits when the last page is reached.
   */
  @Output('last')
  public lastMount: EventEmitter<number> = new EventEmitter<number>();

  /**
   * @getter classStyleVariant
   * @description Returns variant name mapped via enum helper.
   */
  public get classStyleVariant(): string | null {
    return this.variant ?? null;
  }

  /**
   * @getter paginationVariant
   * @description Full class name for variant styling.
   */
  public get paginationVariant(): string {
    let base = "pagination";
    if (this.isOutline) base += "-outline";
    return this.classStyleVariant ? `${base}-${this.classStyleVariant}` : '';
  }

  /**
   * @getter classStyleRounded
   * @description Returns class for rounded style if enabled.
   */
  public get classStyleRounded(): string {
    return this.rounded ? 'pagination-rounded' : '';
  }

  /**
   * @getter classStyleSquare
   * @description Returns class for square style if enabled.
   */
  public get classStyleSquare(): string {
    return this.square ? 'pagination-square' : '';
  }

  /**
   * @getter classStyleSize
   * @description Returns class based on pagination size.
   */
  public get classStyleSize(): string {
    return this.size ? `pagination-${this.size}` : '';
  }

  /**
   * @getter bgVariant
   * @description Returns background variant class.
   */
  public get bgVariant(): string {
    return this.classStyleVariant ? `bg-${this.classStyleVariant}` : 'bg-primary';
  }

  /**
   * @getter textVariant
   * @description Returns text variant class.
   */
  public get textVariant(): string {
    return this.classStyleVariant ? `text-${this.classStyleVariant}` : 'text-primary';
  }

  /**
   * @description Index of the visible page window.
   */
  public indexPage: number = 0;

  /**
   * @getter isFirstPage
   * @description Checks if current page is the first one.
   */
  public get isFirstPage(): boolean {
    return this.currentPage === 1;
  }

  /**
   * @getter isLastPage
   * @description Checks if current page is the last one.
   */
  public get isLastPage(): boolean {
    return this.currentPage === this.lastPage;
  }

  /**
   * @getter lastPage
   * @description Gets the last page number.
   */
  public get lastPage(): number {
    return this.totalPages[this.totalPages.length - 1];
  }

  /**
   * @getter totalPages
   * @description Returns array of all page numbers.
   */
  public get totalPages(): number[] {
    const length = this._calcTotalPages(this.itemsPerPage, this.totalRecords);
    return Array.from({ length }, (_, i) => i + 1);
  }

  /**
   * @getter totalPageCount
   * @description Gets total number of pages.
   */
  public get totalPageCount(): number {
    return this._calcTotalPages(this.itemsPerPage, this.totalRecords);
  }

  /**
   * @getter frazzled
   * @description Returns true if total page count is null or not a number or less than 0 or disabled is true
   */
  public get frazzled(): boolean {
    return Boolean(this.totalPageCount <= 0) || this.disabled;
  }

  constructor() {}

  /**
   * @lifecycle ngOnChanges
   * @description Triggers page update logic when input changes.
   */
  ngOnChanges(changes: SimpleChanges): void {

    // Resize the pager *before* applying a page: a search or page-size change
    // can shrink the range under the active page and the visible window, and
    // the `currentPage` setter silently drops values that no longer fit.
    if ('totalRecords' in changes || 'itemsPerPage' in changes) this._syncToPageRange();

    if ('currentPage' in changes) this.onPageChange(this.currentPage, true);
  }

  /**
   * @lifecycle ngOnInit
   */
  ngOnInit(): void {}

  /**
   * @method isCurrentPage
   * @description Checks if given page is the current one.
   */
  public isCurrentPage(page: number): boolean {
    return page === this.currentPage;
  }

  /**
   * @method onFirstPageInRow
   * @description Navigates to the first page of the visible range.
   */
  public onFirstPageInRow(): void {
    const newIndex = this._clampIndex(this.indexPage - this.rows);
    this.onPageChange(this.totalPages[newIndex] ?? 1);
  }

  /**
   * @method onLastPageInRow
   * @description Navigates to the last page of the visible range.
   */
  public onLastPageInRow(): void {

    // Calculate the index of the first page in the next row
    const nextRowStartIndex = this.indexPage + this.rows;
    
    // If we're already at or past the last row, go to last page
    if (nextRowStartIndex >= this.totalPages.length) {
      this.onPageChange(this.lastPage);
      return;
    }

    // Otherwise, navigate to the first page of the next row
    const newIndex = this._clampIndex(nextRowStartIndex);
    const targetPage = this.totalPages[newIndex];
    this.onPageChange(targetPage ?? this.lastPage);

  }

  /**
   * @method onFirstPage
   * @description Navigates to the first page.
   */
  public onFirstPage(): void {
    this.onPageChange(1);
  }

  /**
   * @method nextPage
   * @description Navigates to the next page if not at last.
   */
  public nextPage(): void {
    if (!this.isLastPage) this.onPageChange(this.currentPage + 1);
  }

  /**
   * @method previousPage
   * @description Navigates to the previous page if not at first.
   */
  public previousPage(): void {
    if (!this.isFirstPage) this.onPageChange(this.currentPage - 1);
  }

  /**
   * @method onLastPage
   * @description Navigates to the last page.
   */
  public onLastPage(): void {
    
    this.onPageChange(this.lastPage);
  }

  /**
   * @method onPageChange
   * @description
   * Updates the current page, emits events, and recalculates visible window if necessary.
   * Triggers jump logic if user navigates outside the current visible page range.
   *
   * @param page Target page to navigate to.
   * @param init Whether this change was from initialization (skip redundant logic).
   */
  public onPageChange(page: number, init: boolean = false): void {

    // void
    if (this.disabled || this.frazzled) return;

    // void
    if (page == null || (!init && this.currentPage === page)) return;

    this.currentPage = page;

    const pageIndex = this.totalPages.findIndex(p => p === page);
    const visibleStart = this.indexPage;
    const visibleEnd = this.indexPage + this.rows - 1;

    if (pageIndex === visibleEnd || pageIndex === visibleStart || pageIndex < visibleStart || pageIndex > visibleEnd) {
      this.indexPage = this._clampIndex(pageIndex - (pageIndex === visibleEnd ? 0 : this.rows - 1));
    }

    this.pageChange.emit(this.currentPage);

    if (this.isFirstPage) this.firstMount.emit(this.currentPage);
    if (this.isLastPage) this.lastMount.emit(this.currentPage);

  }

  /**
   * @method _syncToPageRange
   * @description
   * Pulls the active page and the visible page window back inside the current
   * range after `totalRecords` / `itemsPerPage` changed it.
   *
   * Without this, filtering ten pages down to one leaves `indexPage` parked on
   * the old 6–10 window and the template's slice renders no page buttons at
   * all. Deliberately does **not** emit `pageChange` — the owning state is what
   * decides which page to actually load, and echoing back here would race it
   * into a duplicate fetch.
   */
  private _syncToPageRange(): void {

    const lastPage = this.totalPageCount;

    // no pages to show (empty or unloaded result set)
    if (lastPage <= 0) {
      this._currentPage = 1;
      this.indexPage = 0;
      return;
    }

    // the range may have shrunk past the active page
    if (this._currentPage > lastPage) this._currentPage = lastPage;

    const pageIndex = this._currentPage - 1;
    const visibleStart = this.indexPage;
    const visibleEnd = this.indexPage + this.rows - 1;

    // re-anchor the window on the active page only when it fell outside it
    this.indexPage = pageIndex < visibleStart || pageIndex > visibleEnd
      ? this._clampIndex(pageIndex - this.rows + 1)
      : this._clampIndex(this.indexPage);
  }

  /**
   * @method _clampIndex
   * @description Ensures row index stays within page range.
   */
  private _clampIndex(value: number): number {
    return Math.max(0, Math.min(value, this.totalPages.length - this.rows));
  }

  /**
   * @method _calcTotalPages
   * @description Calculates total page count based on item size and total.
   */
  private _calcTotalPages(pagesSize: number, totalItems: number): number {
    if (pagesSize == null || typeof pagesSize !== 'number') return 0;
    if (totalItems == null || typeof totalItems !== 'number') return 0;
    return Math.ceil(totalItems / pagesSize);
  }

}
